import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { useRolePerms } from '../../features/auth/useRolePerms.js';

interface Notif {
  id?: string;
  read?: boolean;
  category: string;
  severity: 'info' | 'warn' | 'danger';
  title: string;
  body: string;
  entityType: string;
  entityId: string;
}

interface NotifFeed {
  unreadCount: number;
  notifications: Notif[];
}

const CATEGORY_ROUTE: Record<string, string> = {
  expired: '/training',
  expiring: '/training',
  non_compliant: '/suppliers',
  unsigned: '/docsign',
  geofence: '/compliance',
  availability: '/availsignals',
  doc_to_sign: '/docsign',
};

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [readAt, setReadAt] = useState<number | null>(null);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { isSiteUser } = useRolePerms();
  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<NotifFeed>('/notifications').then((r) => r.data),
    refetchInterval: 60_000,
  });

  const unread = data ? (readAt ? 0 : data.unreadCount) : 0;

  const byCategory = (data?.notifications ?? []).reduce<Record<string, Notif[]>>((acc, n) => {
    (acc[n.category] ??= []).push(n);
    return acc;
  }, {});

  const sections: Array<{ key: string; label: string }> = [
    { key: 'expired', label: 'Expired certs' },
    { key: 'expiring', label: 'Expiring certs' },
    { key: 'non_compliant', label: 'Non-compliant suppliers' },
    { key: 'unsigned', label: 'Unsigned documents' },
    { key: 'geofence', label: 'Geofence anomalies' },
    { key: 'availability', label: 'Newly available workers' },
    { key: 'doc_to_sign', label: 'Documents to sign' },
  ];

  const markAllRead = async () => {
    if (isSiteUser) {
      await api.post('/notifications/read');
    } else {
      setReadAt(Date.now());
    }
    qc.invalidateQueries({ queryKey: ['notifications'] });
  };

  return (
    <div style={{ position: 'relative' }}>
      <div className="notif-bell" onClick={() => setOpen((o) => !o)}>
        🔔
        <span className="notif-count" data-count={unread}>{unread}</span>
      </div>
      {open && data ? (
        <div className="notif-panel">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <strong style={{ fontSize: 12 }}>Notifications</strong>
            <button className="btn btn-ghost btn-xs" onClick={markAllRead}>Mark all read</button>
          </div>
          {sections.map((s) => {
            const rows = byCategory[s.key] ?? [];
            if (!rows.length) return null;
            return (
              <div key={s.key}>
                <h4>{s.label} ({rows.length})</h4>
                {rows.slice(0, 10).map((n, i) => (
                  <div key={`${n.entityId}-${i}`} className={`notif-row ${n.severity}`} onClick={() => { navigate(CATEGORY_ROUTE[n.category] ?? '/dashboard'); setOpen(false); }}>
                    <div>
                      <div style={{ fontWeight: n.read ? 400 : 600 }}>{n.title}</div>
                      <div style={{ color: 'var(--muted)', fontSize: 10, marginTop: 2 }}>{n.body}</div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
          {!data.notifications.length ? <div style={{ fontSize: 11, color: 'var(--muted)', padding: 12 }}>Nothing to see here — all clear.</div> : null}
        </div>
      ) : null}
    </div>
  );
}
